import { FileData, Storyboard } from "@/utils/types";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axios from "axios";
import { track } from "@/utils/events/tracker";
import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";
import { JsxElement } from "@/utils/jsxElementTypes";
import { toast } from "sonner";
import {
  getAxiosErorrMessage,
  getUnexpiredToken,
  logAxiosError,
  useActiveCanvas,
  useActiveProject,
  useCanvasId,
} from "@/utils/utils";
import { updateCanvasStoryboards } from "@/store/supabaseSlice";
import { fetchFileTree } from "@/store/codebaseSlice";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { useCanvasStore } from "@/store/zustand/canvasStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useAssetComponents } from "../useAssetComponents";
import { useSession } from "@clerk/clerk-react";
import useActivityStore from "@/store/zustand/activityStore";

export const useSaveStoryboard = () => {
  const dispatch = useDispatch<any>();

  const canvasId = useCanvasId();

  const project = useActiveProject();
  const canvas = useActiveCanvas();

  const { session } = useSession();
  const { supabaseClient } = useSupabase();

  const triggerRefetchActivityStream = useActivityStore(
    (state) => state.triggerRefetchActivityStream
  );

  const treeElementLookup = useCodebaseStore(
    (state) => state.treeElementLookup
  );
  const { componentKeyToComponent } = useAssetComponents();
  const updateLookupsBasedOnPartialResponse = useCodebaseStore(
    (state) => state.updateLookupsBasedOnPartialResponse
  );
  const replaceOpenedFile = useCodebaseStore(
    (state) => state.replaceOpenedFile
  );

  // Get the folders for saving input
  const fileTree: FileData[] = useSelector(
    (state: any) => state.codebase.fileTree
  );

  const [newComponentNameInput, setNewComponentNameInput] = useState("");
  const [insertLocationInput, setInsertLocationInput] = useState("");
  const [storyComponentInput, setStoryComponentInput] = useState("");

  const [showConfirmSave, setShowConfirmSave] = useState<
    null | "component" | "story"
  >(null);
  const [storyboardToSave, setStoryboardToSave] = useState<Storyboard | null>(
    null
  );
  const [saving, setSaving] = useState(false);

  const canvasComponentKey = useCanvasStore(
    (store) => store.canvasComponentKey
  );
  const canvasComponent = // @ts-expect-error TS2538
    componentKeyToComponent?.[canvasComponentKey] as JsxElement;

  const [newStoryExtension, setNewStoryExtension] = useState(".tsx");

  useEffect(() => {
    if (newStoryExtension) return;

    const elements = Object.values(treeElementLookup);
    for (let i = 0; i < elements.length; i++) {
      const ext = elements[i].filename?.split(".").pop();
      if (ext && [".tsx", ".jsx"].includes(ext)) {
        setNewStoryExtension(ext);
        return;
      }
    }
  }, [treeElementLookup]);

  // Note: All file paths are treated as case insensitive on the backend (by FS)
  const { allFolders, allStoryFiles, mostLikelyFolderForComponents } =
    useMemo(() => {
      const allFolders = [];
      const allStoryFiles = [];

      let mostLikelyFolderForComponents = null;

      const getFolders = (fileTree: FileData[]) => {
        fileTree?.forEach((file: FileData) => {
          if (
            file.type === "folder" &&
            !file.fullPath.includes("node_modules") &&
            !file.fullPath.includes("tempobook/dynamic") &&
            !file.fullPath.includes("tempobook/storyboards")
          ) {
            // @ts-expect-error TS2345
            allFolders.push(file.fullPath);
            file.items && getFolders(file.items);

            if (file.fullPath.includes("component")) {
              // @ts-expect-error TS2322
              mostLikelyFolderForComponents = file.fullPath;
            }
          } else if (
            file.type === "file" &&
            file.fullPath.includes("stories.")
          ) {
            // @ts-expect-error TS2345
            allStoryFiles.push(file.fullPath);
          }
        });
      };
      getFolders(fileTree);

      return { allFolders, allStoryFiles, mostLikelyFolderForComponents };
    }, [fileTree]);

  const allSaveAsStoryLocationOptions = useMemo(() => {
    let storySb;

    // Find a story storyboard in this particular component canvas
    if (canvasComponent && canvas) {
      storySb = canvas.storyboards.find(
        (sb: any) =>
          sb.type === STORYBOARD_TYPES.STORY && !sb.deleted && sb.saved
      );
    }

    let existingStoryFilesToUse = allStoryFiles;

    // This is a component with no stories yet, only allow creating new files
    if (Boolean(canvasComponent) && showConfirmSave === "story" && !storySb) {
      existingStoryFilesToUse = [];
    }

    const lowercaseAllStoryFiles = allStoryFiles.map((folder) =>
      // @ts-expect-error TS2339
      folder.toLowerCase()
    );

    const options = existingStoryFilesToUse
      .concat(
        // @ts-expect-error TS2769
        storyComponentInput
          ? allFolders.map((folder) => {
              let tryIndex = 2;
              let newStoryFileName = `${folder}/${storyComponentInput.toLowerCase()}.stories${newStoryExtension}`;
              while (
                lowercaseAllStoryFiles.includes(newStoryFileName.toLowerCase())
              ) {
                newStoryFileName = `${folder}/${storyComponentInput.toLowerCase()}-${tryIndex}.stories${newStoryExtension}`;
                tryIndex++;
              }

              return `${newStoryFileName} (create)`;
            })
          : []
      )
      .sort((a: string, b: string) => {
        // First sort by whether they are new files or not
        if (a.includes("(create)") !== b.includes("(create)")) {
          return a.includes("(create)") ? 1 : -1;
        }

        // Next sort by whether they contain the name for the currently set newComponentNameInput
        if (
          storyComponentInput &&
          a.includes(storyComponentInput.toLowerCase()) !==
            b.includes(storyComponentInput.toLowerCase())
        ) {
          return a.includes(storyComponentInput.toLowerCase()) ? -1 : 1;
        }

        // Next sort by if the folder has the word "stories" in it
        if (a.includes("/stories") !== b.includes("/stories")) {
          return a.includes("/stories") ? -1 : 1;
        }

        // Finally sort by depth
        return a.split("/").length - b.split("/").length;
      });

    // @ts-expect-error TS2345
    if (storySb && options.includes(storySb.fullFilePath)) {
      return [storySb.fullFilePath];
    }

    return options;
  }, [
    storyComponentInput,
    allFolders,
    project,
    allStoryFiles,
    canvasComponent,
  ]);

  const mostLikelyFileForStories = allSaveAsStoryLocationOptions?.[0];

  useEffect(() => {
    if (!showConfirmSave) {
      return;
    }

    if (
      showConfirmSave === "component" &&
      // @ts-expect-error TS2345
      allFolders.includes(insertLocationInput)
    ) {
      return;
    }

    if (
      showConfirmSave === "story" &&
      allSaveAsStoryLocationOptions.includes(insertLocationInput)
    ) {
      return;
    }

    if (mostLikelyFileForStories && showConfirmSave === "story") {
      setInsertLocationInput(mostLikelyFileForStories);
    } else if (
      mostLikelyFolderForComponents &&
      showConfirmSave === "component"
    ) {
      setInsertLocationInput(mostLikelyFolderForComponents);
    }
  }, [
    mostLikelyFileForStories,
    mostLikelyFolderForComponents,
    insertLocationInput,
    showConfirmSave,
  ]);

  const possibleComponentsForStory = useMemo(() => {
    if (!storyboardToSave) {
      return [];
    }

    if (canvasComponent) {
      return [canvasComponent.componentName];
    }

    return Array.from(
      new Set(
        treeElementLookup
          ? Object.values(treeElementLookup)
              .filter((treeElement: any) => {
                return (
                  treeElement.filename === storyboardToSave.fullFilePath &&
                  treeElement.type === "component-instance"
                );
              })
              .map((treeElement: any) => treeElement.componentName)
          : []
      )
    );
  }, [
    treeElementLookup,
    storyboardToSave,
    canvasComponent,
    canvasComponentKey,
  ]);

  const existingStoriesInChosenFile = useMemo(() => {
    if (!insertLocationInput) {
      return new Set();
    }

    return new Set(
      treeElementLookup
        ? Object.values(treeElementLookup)
            .filter((treeElement: any) => {
              return (
                treeElement.filename === insertLocationInput &&
                treeElement.type === "storybook-component"
              );
            })
            .map((treeElement: any) => treeElement.componentName)
        : []
    );
  }, [treeElementLookup, insertLocationInput]);

  const saveStoryboard = (
    storyboard: Storyboard,
    saveAs: "component" | "story"
  ) => {
    if (saveAs === "component") {
      const newName = storyboard.name;
      const capitalizedName = newName
        ? newName.charAt(0).toUpperCase() + newName.slice(1)
        : "";
      setNewComponentNameInput(capitalizedName);
      setStoryboardToSave(storyboard);
    } else {
      const newName = storyboard.name?.replace("Storyboard", "Story");
      const capitalizedName = newName
        ? newName.charAt(0).toUpperCase() + newName.slice(1)
        : "";
      setNewComponentNameInput(capitalizedName);
      setStoryComponentInput(
        canvasComponent
          ? canvasComponent.componentName
          : possibleComponentsForStory?.length
          ? possibleComponentsForStory?.[0]
          : null
      );
      setStoryboardToSave(storyboard);
    }

    setSaving(false);
    setShowConfirmSave(saveAs);
  };

  const performSave = async () => {
    if (!session || !canvas || !supabaseClient || !storyboardToSave) {
      return;
    }

    setSaving(true);

    if (!newComponentNameInput || !insertLocationInput) {
      setSaving(false);
      return;
    }

    if (newComponentNameInput === storyComponentInput) {
      toast.error(
        "Cannot create a story that is named the same as the main component it uses"
      );
      setSaving(false);
      return;
    }

    const body = {
      unsavedComponentFilename: storyboardToSave.fullFilePath,
    };

    if (showConfirmSave === "component") {
      body["location"] = insertLocationInput;
      body["prevComponentName"] = storyboardToSave.name;
      body["componentName"] = newComponentNameInput;
    } else {
      body["storyName"] = newComponentNameInput;
      body["storyFullFilePath"] = insertLocationInput.replace(" (create)", "");
      body["storyMainComponent"] = storyComponentInput;
    }

    track("STORYBOARD_SAVE", {
      saved_as: showConfirmSave === "component" ? "component" : "story",
    });

    // Mark the storyboard as saving (so it doesn't get re-created)
    const newStoryboards = canvas.storyboards.map((sb: Storyboard) => {
      if (sb.id === storyboardToSave.id) {
        return {
          ...sb,
          saving: true,
        };
      } else {
        return sb;
      }
    });

    await updateCanvasStoryboards(
      supabaseClient,
      dispatch,
      newStoryboards,
      canvas
    );

    const onFail = async () => {
      // Mark it back as not saving
      const newStoryboards = canvas.storyboards.map((sb: Storyboard) => {
        if (sb.id === storyboardToSave.id) {
          return {
            ...sb,
            saving: false,
          };
        } else {
          return sb;
        }
      });

      await updateCanvasStoryboards(
        supabaseClient,
        dispatch,
        newStoryboards,
        canvas
      );
    };

    const supabaseJWT = await getUnexpiredToken(session);

    axios
      .post(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/parseAndMutate/mutate/${
          showConfirmSave === "component"
            ? "saveUnsavedComponent"
            : "saveUnsavedStory"
        }`,
        body,
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        }
      )
      .then(async (res: any) => {
        let createdComponent;
        let newFullFilePath = null;
        let savedCodebaseId = null;

        if (showConfirmSave === "component") {
          createdComponent = Object.values(res.data.treeElementLookup).find(
            (treeElement: any) => treeElement.type === "component"
          );
          newFullFilePath = createdComponent?.filename;
          savedCodebaseId = createdComponent?.codebaseId;
        } else {
          createdComponent = Object.values(res.data.treeElementLookup).find(
            (treeElement: any) =>
              treeElement.type === "storybook-component" &&
              treeElement.componentName === newComponentNameInput
          );
          newFullFilePath = createdComponent?.filename;
          savedCodebaseId = createdComponent?.codebaseId;
        }

        if (newFullFilePath) {
          // Replace the opened file in the code editor
          replaceOpenedFile({
            originalFilename: storyboardToSave.fullFilePath,
            newFilename: newFullFilePath,
          });
        }

        if (!newFullFilePath) {
          toast.error(
            "Component saved but cannot find URL to render it, please re-create the storyboard"
          );
          setSaving(false);
          onFail();
          return;
        }

        // Update all the storyboards that use this component
        const newStoryboards: Storyboard[] = canvas.storyboards.map((sb) => {
          if (sb.fullFilePath === storyboardToSave?.fullFilePath) {
            return {
              ...sb,
              storyComponentName:
                showConfirmSave === "story"
                  ? createdComponent.componentName
                  : sb.storyComponentName,
              saved: true,
              saving: false,
              recentlySavedTs: new Date().toISOString(),
              fullFilePath: newFullFilePath,
              name: newComponentNameInput,
              savedCodebaseId: savedCodebaseId || undefined,
              storyboardComponentCodebaseId: savedCodebaseId || undefined,
            };
          } else {
            return sb;
          }
        });

        await updateCanvasStoryboards(
          supabaseClient,
          dispatch,
          newStoryboards,
          canvas
        );

        setShowConfirmSave(null);
        setSaving(false);
        dispatch(
          fetchFileTree({
            canvasId,
            session,
          })
        );

        // This action closes deleted files, so replacing the open file has to be done before it
        // It also updates the canvas based on the stories, so we need to update the canvas first
        updateLookupsBasedOnPartialResponse(res.data);
        triggerRefetchActivityStream();
      })
      .catch((err) => {
        setSaving(false);
        onFail();
        logAxiosError(
          `Could not save ${
            showConfirmSave === "component" ? "component" : "story"
          }`,
          err
        );
        toast.error(
          `Could not save ${
            showConfirmSave === "component" ? "component" : "story"
          }: ` + getAxiosErorrMessage(err)
        );
      });
  };

  const renderConfirmSaveAsModal = () => {
    return (
      <Dialog
        open={Boolean(showConfirmSave)}
        onOpenChange={() => {
          setShowConfirmSave(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Save As {showConfirmSave === "component" ? "Component" : "Story"}
            </DialogTitle>
            <DialogDescription className="text-secondary-900">
              Save this storyboard as a{" "}
              {showConfirmSave === "component" ? "component" : "story"} to reuse
              it in other storyboards.
            </DialogDescription>
            {showConfirmSave === "story" &&
              possibleComponentsForStory.length === 0 && (
                <div
                  className="text-sm"
                  style={{
                    // Peter: I couldn't figure out why tailwind classes were not working for styling so I just set this (amber-500)
                    color: "#f59e0b",
                  }}
                >
                  Warning: No components found in this storyboard, are you sure
                  you want to save this as a story?
                </div>
              )}
            {showConfirmSave === "story" &&
              existingStoriesInChosenFile.has(newComponentNameInput) && (
                <div className="text-destructive text-sm">
                  Error: Story name already in use, choose a different name
                </div>
              )}
            {showConfirmSave === "story" &&
              newComponentNameInput === storyComponentInput && (
                <div className="text-destructive text-sm">
                  Error: Story cannot be named the same as the main component it
                  uses
                </div>
              )}
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-left">
                Name
              </Label>
              <Input
                id="name"
                value={newComponentNameInput}
                className="col-span-3"
                onChange={(e) => {
                  const newVal = e.target.value;

                  if (!newVal) {
                    setNewComponentNameInput("");
                    return;
                  }

                  let newInput = newVal;
                  // First character has to be a letter
                  newInput = newInput.replace(/^[^a-zA-Z]+/, "");
                  // Otherwise all the other characters have to be letters or numbers
                  newInput = newInput.replace(/[^a-zA-Z0-9]+/g, "");

                  setNewComponentNameInput(
                    newInput.charAt(0).toUpperCase() + newInput.substring(1)
                  );
                }}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="location" className="text-left">
                Location
              </Label>
              <Select
                value={insertLocationInput}
                onValueChange={(newVal) => setInsertLocationInput(newVal)}
                disabled={
                  Boolean(canvasComponent) &&
                  showConfirmSave === "story" &&
                  allSaveAsStoryLocationOptions?.length === 1
                }
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Select a folder" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {(showConfirmSave === "component"
                    ? allFolders
                    : allSaveAsStoryLocationOptions
                  ).map((folder) => {
                    return (
                      <SelectItem key={folder} value={folder}>
                        /{folder.split(canvasId + "/")[1]}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            {showConfirmSave === "story" && (
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="main-component" className="text-left">
                  Base Component
                </Label>
                <Select
                  value={storyComponentInput}
                  onValueChange={(newVal) => setStoryComponentInput(newVal)}
                  disabled={
                    !possibleComponentsForStory.length ||
                    Boolean(canvasComponent)
                  }
                >
                  <SelectTrigger className="w-[180px]">
                    {possibleComponentsForStory.length ? (
                      <SelectValue
                        placeholder={
                          possibleComponentsForStory?.length
                            ? "Select a component"
                            : "No component found"
                        }
                      />
                    ) : (
                      "No component found"
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {possibleComponentsForStory.map((compName) => {
                        return (
                          <SelectItem key={compName} value={compName}>
                            {compName}
                          </SelectItem>
                        );
                      })}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="secondary"
              disabled={saving}
              onClick={() => setShowConfirmSave(null)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                saving || !newComponentNameInput || !insertLocationInput
              }
              onClick={() => {
                performSave();
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  return {
    saveStoryboard,
    renderConfirmSaveAsModal,
  };
};
